import { createSlice } from '@reduxjs/toolkit'; 

const getProductsFromSearchId = (searchId, hist) => {
  // collect products from all sources for this search_id, dedupe by product_id
  const allSearches = hist?.searchesProducts.searches.filter(f => f.search_id === searchId)
  if (!allSearches || allSearches.length === 0) return []
  const seen = new Set()
  const p = allSearches.flatMap(s => s.products || []).filter(e => {
    if (!e.product_id) return false
    if (seen.has(e.product_id)) return false
    seen.add(e.product_id)
    return true
  })

  if (!p || p.length === 0) return []

  const rankedProducts = p.map((e) => {
    const src = e.source === 'circle' ? 'Target' : e.source
    const rawMedias = e.medias || []
    const images = rawMedias.map(m => typeof m === 'string' ? m : m.media_url).filter(Boolean)
    return {
      id: src + '-' + e.product_id,
      images,
      score: e.score || 0,
      product_data: {
        id: e.product_id,
        source: src,
        title: e.title,
        price: e.price,
        discounted_price: e.discounted_price,
        currency: e.product_currency,
        description: e.description,
        brand: e.brand,
        rating: e.rating,
        source_url: e.source_url,
        product_code: e.product_code,
        color_name: e.color_name,
        size: e.size,
        short_description: e.short_description,
      }
    }
  }).sort((a, b) => b.score - a.score || (b.product_data.rating || 0) - (a.product_data.rating || 0))

  return rankedProducts
}

const getDiscourseFromSearchId = (searchId, history) => {
  const session_id = history.searchesProducts.searches.find(f => f.search_id === searchId)?.session_id
  const sessionEntry = history.sessionMessages?.find(e => e.session_id === session_id)
  if (!sessionEntry) return []
  const messages = [...sessionEntry.messages].sort((a, b) => parseInt(a.seq) - parseInt(b.seq))
  return messages.map((e, i) => ({
    actor: i % 2 === 0 ? 'user' : 'ai',
    message: e.message_body,
    timestamp: e.created_at
  }))
}

const buildSessionGroups = (rawSearches) => {
  const seenIds = new Set()
  const allSearches = rawSearches
    .map(s => ({
      session_id: s.session_id,
      search_id: s.search_id,
      search_summary: s.search_summary || s.search_string,
      created_at: s.created_at
    }))
    .filter(s => {
      if (!s.search_summary) return false
      if (seenIds.has(s.search_id)) return false
      seenIds.add(s.search_id)
      return true
    })
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

  const sessionMap = new Map()
  allSearches.forEach(s => {
    if (!sessionMap.has(s.session_id)) sessionMap.set(s.session_id, [])
    sessionMap.get(s.session_id).push(s)
  })

  const sessionGroups = Array.from(sessionMap.entries())
    .map(([session_id, searches]) => ({
      session_id,
      searches,
      latest_at: searches[searches.length - 1].created_at,
    }))
    .sort((a, b) => new Date(b.latest_at) - new Date(a.latest_at))

  return { sessionGroups }
}

export const dataSlice = createSlice({
  name: 'data',
  initialState: {
    isInside: false,
    discourse: [],
    searching: false,
    products: null,
    history: null,
    searches: null,
    sessionGroups: [],
    hasMoreHistory: false,
    historyOffset: 0,
    chatSessionId: null,
    autoSearchedMessageIds: []
  },
  reducers: {
    setChatSessionId: (state, action) => {
      return {
        ...state,
        chatSessionId: action.payload
    }

    },
    setInside: (state, action) => {
      //console.log('dataSlice: setInside reducer:', action.payload)
        return {
            ...state,
            isInside: action.payload ? true : false
        }
    },
    reloadDataPerSearchId: (state, action) => {
      const searchId = action.payload
      const historyCopy = JSON.parse(JSON.stringify(state.history))
      const productsCopy = JSON.parse(JSON.stringify(state.products))
      const iProducts = getProductsFromSearchId(searchId, historyCopy)
      const dis = getDiscourseFromSearchId(searchId, historyCopy)

      return {
        ...state,
        products: iProducts,
        discourse: dis
      }

    },
    setHistory: (state, action) => {
      const sp = action.payload?.searchesProducts
      const { sessionGroups } = buildSessionGroups(sp?.searches || [])
      const iSearches = sessionGroups.flatMap(g => g.searches)
      return {
        ...state,
        history: action.payload,
        searches: iSearches,
        sessionGroups,
        hasMoreHistory: sp?.hasMore ?? false,
        historyOffset: sp?.searches?.length || 0,
      }
    },
    appendHistory: (state, action) => {
      const newSearches = action.payload?.searches || []
      const { sessionGroups: newGroups } = buildSessionGroups(newSearches)

      // Build a fresh map of existing groups (deep-copy each to avoid Immer proxy mutation)
      const existingMap = new Map(
        state.sessionGroups.map(g => [g.session_id, { ...g, searches: [...g.searches] }])
      )

      newGroups.forEach(ng => {
        if (existingMap.has(ng.session_id)) {
          const ex = existingMap.get(ng.session_id)
          const seen = new Set(ex.searches.map(s => s.search_id))
          const merged = [...ex.searches, ...ng.searches.filter(s => !seen.has(s.search_id))]
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          existingMap.set(ng.session_id, {
            ...ex,
            searches: merged,
            latest_at: merged[merged.length - 1].created_at,
          })
        } else {
          existingMap.set(ng.session_id, ng)
        }
      })

      const merged = Array.from(existingMap.values())
        .sort((a, b) => new Date(b.latest_at) - new Date(a.latest_at))

      return {
        ...state,
        sessionGroups: merged,
        searches: merged.flatMap(g => g.searches),
        hasMoreHistory: action.payload?.hasMore ?? false,
        historyOffset: (state.historyOffset || 0) + newSearches.length,
      }
    },
    setSearching: (state, action) => {
        return {
            ...state,
            searching: action.payload
        }
    },
    clearData: (state, action) => {
      return {
        ...state,

        discourse: [],
        searching: false,
        products: null,
        chatSessionId: null,
        autoSearchedMessageIds: []
    }

    },
    loadDiscourse: (state, action) => {
      return {
        ...state,
        discourse: action.payload
      }
    },
    addToDiscourse: (state, action) => {
      const cc = [...state.discourse]
      const newDiscourse = action.payload
      newDiscourse.timestamp = new Date()
      cc.push(action.payload)
      return {
          ...state,
          discourse: cc
      }
    },
    markAutoSearched: (state, action) => {
      const messageId = action.payload
      if (messageId == null) return state
      if (state.autoSearchedMessageIds.includes(messageId)) return state
      return {
          ...state,
          autoSearchedMessageIds: [...state.autoSearchedMessageIds, messageId]
      }
    },
    setProducts: (state, action) => {
      try {
        const rankedProducts = action.payload.map((e, i) => { 
          e.id = e.product_data?.source + '-' + e.product_data?.id;
          if (e.product_data.source === 'circle') e.product_data.source = 'Target'
          return e}).sort( (a, b) =>  b.score - a.score || b.product_data.rating - a.product_data.rating )
        return {
            ...state,
            products: rankedProducts
        }
      } catch (ex) {
        return { ...state, products: []}

      }
    },

  }
});

export const {
  addToDiscourse,
  setInside,
  setSearching,
  setHistory,
  appendHistory,
  setProducts,
  clearData,
  loadDiscourse,
  setChatSessionId,
  reloadDataPerSearchId,
  markAutoSearched
} = dataSlice.actions;

export const selectIsInside = (state) => state.data.isInside;
export const selectDiscourse = (state) => state.data.discourse;
export const selectSearching = (state) => state.data.searching;
export const selectProducts = (state) => state.data.products;
export const selectHistory = (state) => state.data.history;
export const selectSearches = (state) => state.data.searches;
export const selectChatSessionId = (state) => state.data.chatSessionId;
export const selectAutoSearchedMessageIds = (state) => state.data.autoSearchedMessageIds || [];
export const selectSessionGroups = (state) => state.data.sessionGroups || [];
export const selectHasMoreHistory = (state) => state.data.hasMoreHistory || false;
export const selectHistoryOffset = (state) => state.data.historyOffset || 0;

export default dataSlice.reducer;